import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Upload, X,Info } from "lucide-react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { DsAlert, DsButton, DsSpinner, DsLabel } from "../../../design-system";
import { attendanceService } from "../../../services/api";
import {
  ImportValidationReview,
  buildImportValidationResult,
  downloadValidationStatusFromRows,
} from "../../Common/import-validation";

const HEADER_MAP = [
  { key: "slNo", re: /^(sl\.?\s*no|s\.?\s*no|serial|sr\.?\s*no|#)$/i },
  { key: "employeeCode", re: /^(employee\s*code|emp\s*code|code)$/i },
  { key: "employeeName", re: /^(employee\s*name|emp\s*name|name)$/i },
  { key: "attendanceDate", re: /^(attendance\s*date|att\s*date|date)$/i },
  {
    key: "punchDateTime",
    re: /^(punch\s*time|time|punch\s*date\s*time|punch\s*datetime|punched\s*at|date\s*time|datetime)$/i,
  },
  {
    key: "punchKind",
    re: /^(punch\s*type|punch\s*kind|kind|type|in\s*\/?\s*out)$/i,
  },
  { key: "deviceId", re: /^(device\s*id|device|deviceid)$/i },
  { key: "remarks", re: /^(remarks|notes|reason|comments)$/i },
];

function normalizeHeader(value) {
  return String(value || "").trim().toLowerCase();
}

/** Pad 0–99 as 2 digits. */
function pad2(n) {
  return String(Math.max(0, Number(n) || 0)).padStart(2, "0");
}

const EXCEL_EPOCH_UTC_MS = Date.UTC(1899, 11, 30);

/**
 * Excel serial → YYYY-MM-DD (UTC day count from 1899-12-30).
 * Use Math.round so IST/template timezone fractions (e.g. 46241.77 → 46242)
 * land on the intended calendar day — Math.floor alone shifts many dates back 1 day;
 * local getDate() on SheetJS Date objects can shift forward or back near midnight.
 */
function excelSerialToYmd(serial) {
  const n = Number(serial);
  if (!Number.isFinite(n)) return "";
  const dayNum = Math.round(n);
  const d = new Date(EXCEL_EPOCH_UTC_MS + dayNum * 86400000);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * Wire-safe YYYY-MM-DD from Excel serial / Date / text.
 * Prefer serial math over local Date getters (SheetJS IST day-shift).
 */
function normalizeAttendanceDateCell(value) {
  if (value == null || value === "") return "";
  if (typeof value === "number" && Number.isFinite(value)) {
    return excelSerialToYmd(value);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return excelSerialToYmd((value.getTime() - EXCEL_EPOCH_UTC_MS) / 86400000);
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const dmy = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) return `${dmy[3]}-${pad2(dmy[2])}-${pad2(dmy[1])}`;
  return s;
}

/**
 * Wire-safe HH:mm:ss from Excel time Date / fraction / text.
 * Rounds to nearest second so Excel float (09:14:59.999) does not become 09:14 in the UI.
 */
function normalizePunchTimeCell(value) {
  if (value == null || value === "") return "";
  if (typeof value === "number" && Number.isFinite(value)) {
    // Time-only fraction, or date+time serial — use fractional day only
    const fraction = value >= 0 ? value % 1 : 0;
    const totalSeconds = Math.round((fraction * 86400000) / 1000);
    const hour = Math.floor(totalSeconds / 3600) % 24;
    const minute = Math.floor((totalSeconds % 3600) / 60);
    const second = totalSeconds % 60;
    return `${pad2(hour)}:${pad2(minute)}:${pad2(second)}`;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    // Prefer clock from Excel serial fraction when Date is timezone-skewed
    const serial = (value.getTime() - EXCEL_EPOCH_UTC_MS) / 86400000;
    const fraction = serial >= 0 ? serial % 1 : 0;
    if (fraction > 0.000001) {
      const totalSeconds = Math.round((fraction * 86400000) / 1000);
      const hour = Math.floor(totalSeconds / 3600) % 24;
      const minute = Math.floor((totalSeconds % 3600) / 60);
      const second = totalSeconds % 60;
      return `${pad2(hour)}:${pad2(minute)}:${pad2(second)}`;
    }
    const rounded = new Date(Math.round(value.getTime() / 1000) * 1000);
    return `${pad2(rounded.getHours())}:${pad2(rounded.getMinutes())}:${pad2(rounded.getSeconds())}`;
  }
  const s = String(value).trim();
  // 09.15.00 / 09:15 / 09:15:00
  const timeOnly = s.match(/^(\d{1,2})[:.](\d{2})(?:[:.](\d{2}))?$/);
  if (timeOnly) {
    return `${pad2(timeOnly[1])}:${pad2(timeOnly[2])}:${pad2(timeOnly[3] || 0)}`;
  }
  // Full datetime → keep time portion locally if parseable
  const asDate = new Date(s);
  if (!Number.isNaN(asDate.getTime()) && /\d{4}-\d{2}-\d{2}|T/.test(s)) {
    const rounded = new Date(Math.round(asDate.getTime() / 1000) * 1000);
    return `${pad2(rounded.getHours())}:${pad2(rounded.getMinutes())}:${pad2(rounded.getSeconds())}`;
  }
  return s;
}

function parseSheetToRows(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: false });
        const sheetName =
          workbook.SheetNames.find((n) => /check/i.test(n)) || workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const matrix = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          raw: true,
        });
        if (!matrix.length) {
          resolve({ rows: [] });
          return;
        }
        const headers = (matrix[0] || []).map((h) => String(h || "").trim());
        const colIndex = {};
        headers.forEach((header, idx) => {
          const match = HEADER_MAP.find((m) => m.re.test(normalizeHeader(header)));
          if (match && colIndex[match.key] == null) colIndex[match.key] = idx;
        });
        if (colIndex.employeeCode == null) {
          reject(new Error("Sheet must include an Employee Code column."));
          return;
        }
        if (colIndex.punchDateTime == null) {
          reject(new Error("Sheet must include a Punch Time column."));
          return;
        }
        if (colIndex.punchKind == null) {
          reject(new Error("Sheet must include a Punch Type column (IN/OUT)."));
          return;
        }
        const rows = [];
        for (let i = 1; i < matrix.length; i += 1) {
          const cells = matrix[i] || [];
          const blank = cells.every((c) => c == null || String(c).trim() === "");
          if (blank) continue;
          const rawAt = (key) => {
            const idx = colIndex[key];
            return idx == null ? "" : cells[idx];
          };
          rows.push({
            __row: i + 1,
            slNo: rawAt("slNo"),
            employeeCode: String(rawAt("employeeCode") ?? "").trim(),
            employeeName: String(rawAt("employeeName") ?? "").trim(),
            attendanceDate: normalizeAttendanceDateCell(rawAt("attendanceDate")),
            punchTime: normalizePunchTimeCell(rawAt("punchDateTime")),
            punchDateTime: normalizePunchTimeCell(rawAt("punchDateTime")),
            punchKind: String(rawAt("punchKind") ?? "").trim(),
            deviceId: String(rawAt("deviceId") ?? "").trim(),
            remarks: String(rawAt("remarks") ?? "").trim(),
          });
        }
        resolve({ rows });
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function ChecksImportModal({ isOpen, onClose }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [replaceDayPunches, setReplaceDayPunches] = useState(false);
  const [confirmCancelApprovedCompOff, setConfirmCancelApprovedCompOff] =
    useState(false);
  const [review, setReview] = useState(null);
  const [validatedPayload, setValidatedPayload] = useState(null);
  const [dayInfos, setDayInfos] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setReplaceDayPunches(false);
    setConfirmCancelApprovedCompOff(false);
    setReview(null);
    setValidatedPayload(null);
    setDayInfos([]);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  }, [isOpen]);

  const impactNote = useMemo(() => {
    if (!dayInfos.length) return "";
    return dayInfos
      .slice(0, 8)
      .map(
        (d) =>
          `${d.employeeCode} ${d.attendanceDate}: ${(d.infos || []).join("; ")}`
      )
      .join("\n");
  }, [dayInfos]);

  if (!isOpen || typeof document === "undefined") return null;

  const handleDownloadTemplate = async () => {
    try {
      setBusy(true);
      const res = await attendanceService.downloadChecksImportTemplate();
      const blob = res?.data instanceof Blob ? res.data : new Blob([res.data || res]);
      triggerBlobDownload(blob, "PaceHRM_Import_Checks_Template.xlsx");
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Template download failed");
    } finally {
      setBusy(false);
    }
  };

  const handleValidate = async () => {
    if (!file) {
      toast.error("Please choose a file first.");
      return;
    }
    try {
      setBusy(true);
      const { rows } = await parseSheetToRows(file);
      if (!rows.length) {
        toast.error("No data rows found in the file.");
        return;
      }
      const res = await attendanceService.importEmployeeChecks({
        rows,
        dryRun: true,
        replaceDayPunches,
        confirmCancelApprovedCompOff,
      });
      const data = res?.data || res;
      const validRecords = (data.validRecords || []).map((r) => ({
        ...r,
        name: r.employeeName || r.name,
        empCode: r.employeeCode,
        record: r,
      }));
      const invalidRecords = (data.invalidRecords || []).map((r) => ({
        ...r,
        name: r.employeeName || r.name,
        empCode: r.employeeCode,
        record: r,
      }));
      const built = buildImportValidationResult({
        validRecords,
        invalidRecords,
        hardBlockImport: !!data.hardBlockImport,
      });
      setValidatedPayload({ ...data, rows, validRecords, invalidRecords });
      setReview(built);
      setDayInfos(Array.isArray(data.dayInfos) ? data.dayInfos : []);
      if (built.failedCount > 0) {
        toast(`Validated: ${built.successCount} ok, ${built.failedCount} failed`);
      } else {
        toast.success(`All ${built.successCount} punch row(s) passed validation`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Validation failed");
      setReview(null);
      setValidatedPayload(null);
      setDayInfos([]);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!validatedPayload || !review || review.successCount <= 0) {
      toast.error("No successful rows to import.");
      return;
    }
    try {
      setBusy(true);
      const okRows = (validatedPayload.validRecords || [])
        .filter((r) => r && r.employeeCode)
        .map((r) => {
          const original = (validatedPayload.rows || []).find(
            (x) => Number(x.__row) === Number(r.row)
          );
          return (
            original || {
              __row: r.row,
              employeeCode: r.employeeCode,
              employeeName: r.employeeName,
              attendanceDate: r.attendanceDate,
              punchDateTime: r.punchDateTime,
              punchKind: r.punchKind,
              deviceId: r.deviceId,
              remarks: r.remarks,
            }
          );
        });
      const res = await attendanceService.importEmployeeChecks({
        rows: okRows,
        dryRun: false,
        commit: true,
        replaceDayPunches,
        confirmCancelApprovedCompOff,
      });
      const data = res?.data || res;
      toast.success(
        `Imported ${data.imported || 0} check(s) across ${data.dayCount || 0} day(s)`
      );
      if (data.errors?.length) {
        toast.error(`${data.errors.length} day(s) had commit errors — see console`);
        console.warn("Import Checks errors", data.errors);
      }
      onClose?.();
    } catch (err) {
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.message || err?.message || "Import failed";
      if (code === "COMP_OFF_APPROVED") {
        toast.error(`${msg} — tick “Cancel earned/availed Comp Off” and validate again.`);
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-visible rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-[hsl(var(--ds-fg))]">
              Import Checks
            </h2>
            <div className="group/info relative flex-shrink-0">
                <Info
                  size={15}
                  className="cursor-pointer text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                />
                <div className="pointer-events-none absolute left-full top-1/2 z-[9999] ml-2 hidden w-[400px] -translate-y-1/2 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm leading-5 text-[hsl(var(--ds-fg))] shadow-lg group-hover/info:block">
                    Download the template, fill date + punch time + IN/OUT type, validate
                    sequence, then import.
                    <DsAlert tone="info">
                      Default is <strong>append</strong> to existing system checks. Use the replace
                      checkbox only when the file should become the full set of punches for those
                      dates. IN→OUT sequence must be complete (no open IN).
                    </DsAlert>
                </div>
                
            </div>
            
          </div>
          <button
            type="button"
            className="rounded-lg p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
            onClick={() => !busy && onClose?.()}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div className="flex flex-wrap gap-2"></div>
            <DsLabel className="mb-1">Select file</DsLabel>   
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="block w-full text-sm text-[hsl(var(--ds-fg))] file:mr-3 file:rounded-md file:border-0 file:bg-[hsl(var(--ds-primary))] file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-[hsl(var(--ds-primary-fg))]"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setReview(null);
                setValidatedPayload(null);
                setDayInfos([]);
              }}
            />

          <label className="flex items-start gap-2 text-xs text-[hsl(var(--ds-fg))]">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={replaceDayPunches}
              onChange={(e) => {
                setReplaceDayPunches(e.target.checked);
                setReview(null);
                setValidatedPayload(null);
              }}
            />
            <span>
              Erase existing checks for each date in the file and replace with imported
              checks (other days unchanged)
            </span>
          </label>

          <label className="flex items-start gap-2 text-xs text-[hsl(var(--ds-fg))]">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={confirmCancelApprovedCompOff}
              onChange={(e) => {
                setConfirmCancelApprovedCompOff(e.target.checked);
                setReview(null);
                setValidatedPayload(null);
              }}
            />
            <span>
              Cancel earned/availed Comp Off for affected days when required to import
            </span>
          </label>

          {impactNote ? (
            <DsAlert tone="warning">
              <p className="font-medium">Late / early impact (info only)</p>
              <pre className="mt-1 whitespace-pre-wrap text-[11px]">{impactNote}</pre>
            </DsAlert>
          ) : null}

          {busy && !review ? (
            <div className="flex justify-center py-6">
              <DsSpinner />
            </div>
          ) : null}

          {review ? (
            <ImportValidationReview
              result={review}
              busy={busy}
              entityLabel="check"
              onDownloadReport={() =>
                downloadValidationStatusFromRows(
                  review.allRows || [],
                  "Import_Checks_Validation_Status.xlsx"
                )
              }
              onImportAll={handleConfirm}
              onImportSuccessfulOnly={handleConfirm}
              onBack={() => {
                setReview(null);
                setValidatedPayload(null);
              }}
            />
          ) : null}
        </div>
        {!review && (
          <div className="flex shrink-0 justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={handleDownloadTemplate}
              className="mr-auto gap-2"
            >
              <Download size={16} />
              Download template
            </DsButton>
            <DsButton
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => onClose?.()}
            >
              Cancel
            </DsButton>
            <DsButton
              type="button"
              variant="primary"
              size="sm"
              disabled={busy || !file}
              onClick={handleValidate}
            >
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              Validate
            </DsButton>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
